import {Component, inject, OnInit, signal} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {Router} from '@angular/router';
import {HeaderService} from "../../../../../services/header.service";
import {ApiService} from "../../../../../services/api.service";
import {AiConfiguration} from "../../../../../shared/interfaces/ai-configuration";
import {AiTestResult} from "../../../../../shared/interfaces/ai-configuration";
import {forkJoin} from 'rxjs';
import {AiService} from "../../../data-access/ai.service";
import {AiTestResponseComponent} from "./input-response/input-response.component";

interface TestResponse {
  inputNumber: number;
  response: string;
  responseTime: number;
}

@Component({
  selector: 'app-ai-configuration-test',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AiTestResponseComponent],
  templateUrl: './ai-configuration-test.component.html',
  styleUrl: './ai-configuration-test.component.scss'
})
export class AiConfigurationTestComponent implements OnInit {
  private headerService = inject(HeaderService);
  private apiService = inject(ApiService);
  private aiService = inject(AiService);
  private fb = inject(FormBuilder);
  private router = inject(Router);

  testForm!: FormGroup;
  currentConfig = signal<AiConfiguration | null>(null);
  isLoading = signal(false);
  isGenerating = signal(false);
  testResponses = signal<TestResponse[]>([]);
  errorMessage = signal<string | null>(null);

  ngOnInit(): void {
    this.headerService.setHeaderIcon("bi bi-cpu");
    this.headerService.setHeaderTitle("KI-Konfiguration testen");
    this.headerService.setHeaderSubTitle('');

    this.initForm();
    this.loadConfiguration();
  }

  private initForm(): void {
    this.testForm = this.fb.group({
      input1: ['', [Validators.required]],
      input2: ['', [Validators.required]],
      input3: ['', [Validators.required]]
    });
  }

  private loadConfiguration(): void {
    this.isLoading.set(true);
    this.apiService.getAiConfigurations().subscribe({
      next: (config) => {
        this.currentConfig.set(config.filter(c => c.is_active)[0]);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Fehler beim Laden der Konfiguration:', error);
        this.errorMessage.set('Keine Konfiguration gefunden. Bitte erstellen Sie zuerst eine Konfiguration.');
        this.isLoading.set(false);
      }
    });
  }

  async generateResponse(index: number): Promise<TestResponse | undefined> {
    if (!this.currentConfig()) {
      this.errorMessage.set('Keine aktive KI-Konfiguration vorhanden.');
      return undefined;
    }

    const controlName = `input${index}` as const;

    if (!this.testForm.get(controlName)) {
      console.warn(`Ungültiger Input-Index: ${index}`);
      return undefined;
    }

    if (this.testForm.get(controlName)?.invalid) {
      this.testForm.get(controlName)?.markAsTouched();
      return undefined;
    }

    const userInput = this.testForm.get(controlName)?.value as string;
    if (!userInput) {
      return undefined;
    }

    this.isGenerating.set(true);
    this.errorMessage.set(null);

    const startTime = Date.now();

    try {
      const response = await this.aiService.generateSolution(userInput);
      const responseTime = Date.now() - startTime;

      const tr: TestResponse = {
        inputNumber: index,
        response,
        responseTime
      };

      // Replace or append the response for this specific input
      const updated = this.testResponses()
        .filter(r => r.inputNumber !== index)
        .concat(tr);

      this.testResponses.set(updated);
      this.saveTestResult(index, userInput, response, responseTime);

      return tr;
    } catch (err) {
      console.error(`Fehler bei Generierung für Input ${index}:`, err);
      const responseTime = Date.now() - startTime;
      const fallbackResponse = `Fehler: ${(err as Error)?.message || 'Unbekannt'}`;

      const tr: TestResponse = {
        inputNumber: index,
        response: fallbackResponse,
        responseTime
      };

      const updated = this.testResponses()
        .filter(r => r.inputNumber !== index)
        .concat(tr);

      this.testResponses.set(updated);
      this.saveTestResult(index, userInput, fallbackResponse, responseTime);

      return tr;
    } finally {
      this.isGenerating.set(false);
    }
  }

  async onGenerate(): Promise<void> {
    if (this.testForm.invalid || !this.currentConfig) {
      return;
    }

    this.isGenerating.set(true);
    this.errorMessage.set('');
    this.testResponses.set([]);

    const inputs = [
      {number: 1, text: this.testForm.value.input1},
      {number: 2, text: this.testForm.value.input2},
      {number: 3, text: this.testForm.value.input3}
    ];

    const promises = inputs.map(async (input) => {
      const startTime = Date.now();
      try {
        const response = await this.aiService.generateSolution(input.text);
        const responseTime = Date.now() - startTime;

        const tr: TestResponse = {
          inputNumber: input.number,
          response,
          responseTime
        }

        this.testResponses.set([...this.testResponses(), tr]);
        this.saveTestResult(input.number, input.text, response, responseTime);

        return tr;
      } catch (err) {
        console.error(`Fehler bei Generierung für Input ${input.number}:`, err);
        const responseTime = Date.now() - startTime;
        const fallbackResponse = `Fehler: ${(err as Error)?.message || 'Unbekannt'}`;

        const tr: TestResponse = {
          inputNumber: input.number,
          response: fallbackResponse,
          responseTime
        };

        this.testResponses.set([...this.testResponses(), tr]);

        // Versuche trotzdem zu speichern
        this.saveTestResult(input.number, input.text, fallbackResponse, responseTime);

        return tr;
      }
    });
    await Promise.all(promises);
    this.isGenerating.set(false);
  }

  private saveTestResult(inputNumber: number, userInput: string, gptResponse: string, responseTime: number): void {
    if (!this.currentConfig()?.id) return;

    const testResult: Omit<AiTestResult, "id"> = {
      ai_configuration_id: this.currentConfig()!.id,
      user_input: userInput,
      input_number: inputNumber,
      gpt_response: gptResponse,
      response_time: responseTime
    };

    this.apiService.saveAiTestResult(testResult).subscribe({
      next: (result) => {
        console.log(`Test-Ergebnis ${inputNumber} gespeichert:`, result);
      },
      error: (error) => {
        console.error(`Fehler beim Speichern des Test-Ergebnisses ${inputNumber}:`, error);
      }
    });
  }

  getResponseForInput(inputNumber: number): TestResponse | undefined {
    return this.testResponses().find(r => r.inputNumber === inputNumber);
  }

  navigateToConfiguration(): void {
    this.router.navigate(['/admin/ai-configuration']);
  }

  resetTest(): void {
    this.testForm.reset();
    this.testResponses.set([]);
    this.errorMessage.set('');
  }
}
